import React, { useEffect, useRef, useState } from "react";
import { api, API_BASE, formatErr } from "@/lib/api";
import { toast } from "sonner";
import {
  Video, Plus, Upload, Trash2, CheckCircle2, X, Loader2, Save, Sparkles,
  Link2, Clock, Settings,
} from "lucide-react";

function resolveVideoUrl(u) {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  // API_BASE already includes '/api'. Just prepend it to the '/media/...'
  // path to get the ingress-routable '/api/media/...' URL. If a legacy
  // record already stores '/api/media/...', strip the redundant '/api'.
  if (u.startsWith("/api/media/")) return `${API_BASE}${u.slice(4)}`;
  if (u.startsWith("/media/")) return `${API_BASE}${u}`;
  return u;
}

function SettingsPanel({ settings, onSaved }) {
  const [form, setForm] = useState({
    max_daily_ads: settings.max_daily_ads,
    coins_per_watch: settings.coins_per_watch ?? 10,
    coin_php_value: settings.coin_php_value ?? 0.10,
    max_coin_discount_pct_per_order: settings.max_coin_discount_pct_per_order ?? 20,
    max_coins_per_order: settings.max_coins_per_order ?? 500,
    max_coins_per_month: settings.max_coins_per_month ?? 2000,
    coin_expiry_months: settings.coin_expiry_months ?? 12,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/ad-settings", {
        max_daily_ads: parseInt(form.max_daily_ads, 10),
        coins_per_watch: parseInt(form.coins_per_watch, 10),
        coin_php_value: parseFloat(form.coin_php_value),
        max_coin_discount_pct_per_order: parseFloat(form.max_coin_discount_pct_per_order),
        max_coins_per_order: parseInt(form.max_coins_per_order, 10),
        max_coins_per_month: parseInt(form.max_coins_per_month, 10),
        coin_expiry_months: parseInt(form.coin_expiry_months, 10),
      });
      toast.success("Marketing Center settings saved.");
      onSaved?.(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="glass-card p-4" data-testid="ad-settings-panel">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold mb-3">
        <Settings size={12} /> Marketing Center · ZenCoin Economy
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="zfd-label">Coins per watch</label>
          <input type="number" step="1" min="1" className="zfd-input"
            value={form.coins_per_watch} onChange={set("coins_per_watch")}
            data-testid="ad-settings-coins-per-watch" />
        </div>
        <div>
          <label className="zfd-label">1 ZenCoin = ₱</label>
          <input type="number" step="0.01" min="0.01" className="zfd-input"
            value={form.coin_php_value} onChange={set("coin_php_value")}
            data-testid="ad-settings-coin-php-value" />
        </div>
        <div>
          <label className="zfd-label">Daily watch quota</label>
          <input type="number" step="1" min="1" className="zfd-input"
            value={form.max_daily_ads} onChange={set("max_daily_ads")}
            data-testid="ad-settings-max-daily" />
        </div>
        <div>
          <label className="zfd-label">Coin expiry (months)</label>
          <input type="number" step="1" min="1" max="60" className="zfd-input"
            value={form.coin_expiry_months} onChange={set("coin_expiry_months")}
            data-testid="ad-settings-coin-expiry" />
        </div>
        <div>
          <label className="zfd-label">Max % of order paid by coins</label>
          <input type="number" step="1" min="0" max="100" className="zfd-input"
            value={form.max_coin_discount_pct_per_order} onChange={set("max_coin_discount_pct_per_order")}
            data-testid="ad-settings-max-pct" />
        </div>
        <div>
          <label className="zfd-label">Absolute cap per order (coins)</label>
          <input type="number" step="1" min="0" className="zfd-input"
            value={form.max_coins_per_order} onChange={set("max_coins_per_order")}
            data-testid="ad-settings-max-per-order" />
        </div>
        <div>
          <label className="zfd-label">Rolling monthly cap (coins)</label>
          <input type="number" step="1" min="0" className="zfd-input"
            value={form.max_coins_per_month} onChange={set("max_coins_per_month")}
            data-testid="ad-settings-max-per-month" />
        </div>
        <button
          onClick={save} disabled={saving}
          className="btn-deploy px-4 py-2.5 inline-flex items-center justify-center gap-2 disabled:opacity-40 self-end"
          data-testid="ad-settings-save"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
        </button>
      </div>
      <div className="text-[11px] text-white/45 mt-3">
        ZenCoins are non-withdrawable — they can only be redeemed as checkout discounts.
        Daily quota resets at UTC 00:00. Coin monthly cap resets on the 1st of each UTC month.
      </div>
    </div>
  );
}

function UploadForm({ onCreated }) {
  const [mode, setMode] = useState("upload"); // upload | url
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [partner, setPartner] = useState("");
  const [duration, setDuration] = useState(45);
  const [priority, setPriority] = useState(100);
  const [videoUrl, setVideoUrl] = useState("");
  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const reset = () => {
    setFile(null); setTitle(""); setPartner(""); setDuration(45);
    setPriority(100); setVideoUrl(""); setThumbnailUrl(""); setProgress(0);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !partner.trim()) return toast.error("Title and partner required.");
    if (duration < 10 || duration > 60) return toast.error("Duration must be between 10 and 60 seconds.");
    setUploading(true);
    try {
      if (mode === "upload") {
        if (!file) { toast.error("Pick a video file."); setUploading(false); return; }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", title.trim());
        fd.append("partner_name", partner.trim());
        fd.append("duration_seconds", String(duration));
        fd.append("priority", String(priority));
        fd.append("is_active", "true");
        const { data } = await api.post("/admin/ad-videos/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
          },
        });
        toast.success(`Uploaded "${data.title}"`);
      } else {
        if (!videoUrl.trim()) { toast.error("Video URL is required."); setUploading(false); return; }
        const { data } = await api.post("/admin/ad-videos", {
          title: title.trim(),
          partner_name: partner.trim(),
          video_url: videoUrl.trim(),
          duration_seconds: duration,
          thumbnail_url: thumbnailUrl.trim(),
          priority,
          is_active: true,
        });
        toast.success(`Added "${data.title}"`);
      }
      reset();
      onCreated?.();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setUploading(false); setProgress(0);
    }
  };

  return (
    <form onSubmit={submit} className="glass-card p-4 space-y-3" data-testid="ad-upload-form">
      <div className="flex items-center gap-2 mb-1">
        <button type="button" onClick={() => setMode("upload")} className={`text-xs px-3 py-1.5 rounded font-bold uppercase tracking-widest ${mode === "upload" ? "bg-[#1E3A8A] text-white" : "text-white/50 hover:bg-white/5"}`} data-testid="ad-upload-mode-file">
          <Upload size={12} className="inline mr-1" /> Upload File
        </button>
        <button type="button" onClick={() => setMode("url")} className={`text-xs px-3 py-1.5 rounded font-bold uppercase tracking-widest ${mode === "url" ? "bg-[#1E3A8A] text-white" : "text-white/50 hover:bg-white/5"}`} data-testid="ad-upload-mode-url">
          <Link2 size={12} className="inline mr-1" /> External URL
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="zfd-label">Title</label>
          <input className="zfd-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex: Holiday flash sale — Storefront" data-testid="ad-form-title" required />
        </div>
        <div>
          <label className="zfd-label">Partner / Advertiser</label>
          <input className="zfd-input" value={partner} onChange={(e) => setPartner(e.target.value)} placeholder="Ex: ZFD Internal" data-testid="ad-form-partner" required />
        </div>
        <div>
          <label className="zfd-label">Duration (seconds, 10–60)</label>
          <input type="number" min="10" max="60" step="1" className="zfd-input" value={duration} onChange={(e) => setDuration(parseInt(e.target.value || "0", 10))} data-testid="ad-form-duration" required />
        </div>
        <div>
          <label className="zfd-label">Priority (higher shows first)</label>
          <input type="number" step="1" className="zfd-input" value={priority} onChange={(e) => setPriority(parseInt(e.target.value || "0", 10))} data-testid="ad-form-priority" />
        </div>
      </div>

      {mode === "upload" ? (
        <div>
          <label className="zfd-label">Video File (max 25 MB, mp4/webm/mov)</label>
          <input
            ref={fileRef}
            type="file" accept="video/mp4,video/webm,video/quicktime"
            className="zfd-input"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="ad-form-file"
          />
          {file && <div className="text-[11px] text-white/55 mt-1">{file.name} — {(file.size / 1024 / 1024).toFixed(2)} MB</div>}
          {uploading && progress > 0 && (
            <div className="mt-2 h-1.5 rounded bg-white/10 overflow-hidden">
              <div className="h-full bg-[#FBBF24]" style={{ width: `${progress}%` }} />
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="zfd-label">Video URL</label>
            <input className="zfd-input" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="https://.../ad.mp4" data-testid="ad-form-video-url" />
          </div>
          <div>
            <label className="zfd-label">Thumbnail URL (optional)</label>
            <input className="zfd-input" value={thumbnailUrl} onChange={(e) => setThumbnailUrl(e.target.value)} placeholder="https://.../poster.jpg" data-testid="ad-form-thumb-url" />
          </div>
        </div>
      )}

      <div className="flex items-center justify-between pt-2 border-t border-white/10">
        <div className="text-[11px] text-white/45">Uploaded videos land under <code className="text-white/70">/media/ad_videos/&lt;id&gt;.mp4</code>.</div>
        <button type="submit" disabled={uploading} className="btn-deploy px-4 py-2 inline-flex items-center gap-2 disabled:opacity-40" data-testid="ad-form-submit">
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} {mode === "upload" ? "Upload & Publish" : "Add Video"}
        </button>
      </div>
    </form>
  );
}

function VideoRow({ v, onChanged }) {
  const [busy, setBusy] = useState(false);
  const toggleActive = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/ad-videos/${v.id}`, { is_active: !v.is_active });
      toast.success(!v.is_active ? "Published" : "Paused");
      onChanged?.();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!confirm(`Delete "${v.title}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/ad-videos/${v.id}`);
      toast.success("Deleted");
      onChanged?.();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <tr className="border-b border-white/5" data-testid={`ad-video-row-${v.id}`}>
      <td className="p-2">
        <div className="w-24 aspect-video bg-black rounded overflow-hidden flex items-center justify-center">
          {v.thumbnail_url ? (
            <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
          ) : <Video size={16} className="text-white/25" />}
        </div>
      </td>
      <td className="p-2">
        <div className="text-white font-bold text-sm">{v.title}</div>
        <div className="text-[11px] text-white/50">{v.partner_name}</div>
        <a href={resolveVideoUrl(v.video_url)} target="_blank" rel="noreferrer" className="text-[10px] text-[#93C5FD] hover:underline break-all">{v.video_url}</a>
      </td>
      <td className="p-2 text-white/70 font-mono-num text-xs"><Clock size={11} className="inline mr-1" />{v.duration_seconds}s</td>
      <td className="p-2 text-white/70 font-mono-num text-xs">{v.priority}</td>
      <td className="p-2">
        <button onClick={toggleActive} disabled={busy} className={`text-[10px] uppercase tracking-widest font-bold rounded px-2 py-1 ${v.is_active ? "bg-emerald-500/15 text-emerald-300 border border-emerald-400/30" : "bg-white/5 text-white/50 border border-white/15"}`} data-testid={`ad-video-toggle-${v.id}`}>
          {v.is_active ? "Active" : "Paused"}
        </button>
      </td>
      <td className="p-2">
        <button onClick={del} disabled={busy} className="text-white/40 hover:text-[#FCA5A5]" title="Delete" data-testid={`ad-video-delete-${v.id}`}>
          <Trash2 size={14} />
        </button>
      </td>
    </tr>
  );
}

export default function AdminAdVideos() {
  const [videos, setVideos] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [v, s] = await Promise.all([
        api.get("/admin/ad-videos"),
        api.get("/admin/ad-settings"),
      ]);
      setVideos(v.data);
      setSettings(s.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading || !settings) return (
    <div className="text-white/50 flex items-center gap-2 p-6"><Loader2 size={16} className="animate-spin" /> Loading…</div>
  );

  return (
    <div className="space-y-5" data-testid="admin-adstation">
      <SettingsPanel settings={settings} onSaved={(s) => setSettings(s)} />
      <UploadForm onCreated={load} />
      <div className="glass-card p-4" data-testid="admin-adstation-table">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold flex items-center gap-1">
            <Sparkles size={12} /> Ad Video Catalog · {videos.length} total, {videos.filter((v) => v.is_active).length} active
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[#FBBF24]">
                <th className="text-left p-2">Thumb</th>
                <th className="text-left p-2">Title / URL</th>
                <th className="text-left p-2">Duration</th>
                <th className="text-left p-2">Priority</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2"></th>
              </tr>
            </thead>
            <tbody>
              {videos.length === 0 ? (
                <tr><td colSpan="6" className="p-6 text-center text-white/40">No videos yet. Upload one above or paste an external URL.</td></tr>
              ) : videos.map((v) => (
                <VideoRow key={v.id} v={v} onChanged={load} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
